import vec2 from '@/helpers/vec2'
import range from '@/helpers/range'
import number from '@/helpers/number'

import { Color, NitrateParticle, FontStyle } from './constants'
import utils from './utils'
import random from './random'

const particles = []

const prng = random.create()

export function getSoilX (cx) {
  return -cx.canvas.width >> 1
}

export function getSoilWidth (cx) {
  return cx.canvas.width
}

export function renderSoil (cx) {
  const { width } = cx.canvas
  const halfWidth = width >> 1
  cx.save()
  cx.beginPath()
  cx.moveTo(-halfWidth, 0)
  cx.lineTo(halfWidth, 0)
  cx.strokeStyle = Color.ROOTS
  cx.stroke()
  cx.fillStyle = Color.SOIL
  cx.fillRect(
    getSoilX(cx),
    0,
    getSoilWidth(cx),
    utils.getTopSoilHeight(cx)
  )
  console.log()
  cx.restore()
}

export function renderNitrateParticles (cx, data) {
  if (!data || !data.layers) {
    return
  }
  const { layers } = data
  cx.save()
  // Downward particles
  for (let index = 0; index < particles.length; index++) {
    const particle = particles[index]
    const layer = layers[particle.layer]
    if (!layer || !particle.shouldRender) {
      continue
    }
    const size = 1 - range.clamp(range.from(vec2.y(particle.position), particle.limit[0], particle.limit[1]), 0, 0.4)
    cx.fillStyle = Color.PRIMARY
    cx.beginPath()
    cx.arc(
      vec2.x(particle.position),
      vec2.y(particle.position),
      NitrateParticle.RADIUS * size,
      0,
      2 * Math.PI
    )
    cx.fill()
  }
  cx.restore()
}

export function renderNitrates (cx, data) {
  if (!data || !data.layers) {
    return
  }
  const { layers } = data
  cx.save()
  for (let index = 0; index < layers.length; index++) {
    const fontStyle = FontStyle.VALUE
    const layer = layers[index]
    const y = Math.round(utils.getTopSoilHeight(cx) / layers.length * index)
    const height = Math.round(utils.getTopSoilHeight(cx) / layers.length)
    cx.fillStyle = Color.NITRATE(layer.NF * 2000)
    cx.fillRect(
      getSoilX(cx),
      y,
      getSoilWidth(cx),
      height
    )
    cx.fillStyle = Color.WHITE
    cx.fillRect(getSoilX(cx), y + height, getSoilWidth(cx), 2)
    cx.font = fontStyle

    const nContentText = number.formatNContent(layer.VNC * 10 * 0.01)

    cx.fillStyle = Color.PRIMARY
    cx.font = fontStyle
    cx.fillText(nContentText, getSoilX(cx) + 32, y + height - 16)

    const { width } = cx.measureText(nContentText)
    cx.fillText('kg N/ha', getSoilX(cx) + 32 + width, y + height - 16)
  }
  cx.restore()
}

export function renderLines (cx) {
  cx.save()
  cx.fillStyle = Color.PRIMARY
  cx.fillRect(
    getSoilX(cx),
    0,
    getSoilWidth(cx),
    2
  )
  cx.restore()
}

export function render (cx, data) {
  renderSoil(cx, data)
  renderNitrates(cx, data)
  renderNitrateParticles(cx, data)
  renderLines(cx)
}

export function update (cx, data) {
  if (!data || !data.layers) {
    return
  }
  const { width } = cx.canvas
  const halfWidth = width >> 1

  const flowingParticlesPerLayer = new Array(data.layers.length + 1)
  const particlesPerLayer = particles.length / data.layers.length + 1
  const limitHeight = Math.round(utils.getTopSoilHeight(cx) / data.layers.length)
  // Updates particle position.
  for (let index = 0; index < particles.length; index++) {
    const particle = particles[index]
    particle.layer = Math.floor(range.from(index, 0, particlesPerLayer))

    const layer = data.layers[particle.layer]

    // Calculate number of particles to be rendered
    if (layer.NF !== 0) {
      flowingParticlesPerLayer[particle.layer] = Math.round(range.fromTo(number.exponent(layer.NF), 0, -7, particlesPerLayer, 0))
    }

    particle.shouldRender = index < (particle.layer * particlesPerLayer) + flowingParticlesPerLayer[particle.layer]

    // We increment the particle position by it's velocity.
    if (layer.NF >= 0) {
      vec2.add(particle.position, particle.position, particle.velocity)
    } else {
      vec2.substract(particle.position, particle.position, particle.velocity)
    }
    particle.layer = Math.floor(range.from(index, 0, particlesPerLayer))
    particle.limit[0] = limitHeight * particle.layer
    if (particle.layer < data.layers.length - 1) {
      particle.limit[1] = limitHeight * (particle.layer + 1)
    } else {
      particle.limit[1] = limitHeight * (particle.layer + 2)
    }
    // If the Nparticle gets below the limit then we reset it's position.
    if (layer.NF >= 0 && vec2.y(particle.position) > prng.between(particle.limit[1] * 0.8, particle.limit[1] * 1.2)) {
      vec2.set(particle.position,
        prng.normal(-halfWidth, halfWidth),
        particle.limit[0]
      )
    } else if (layer.NF < 0 && vec2.y(particle.position) < particle.limit[0]) {
      vec2.set(particle.position,
        prng.normal(-halfWidth, halfWidth),
        particle.limit[1]
      )
    }
  }
}

export function init (cx) {
  const { width } = cx.canvas
  const halfWidth = width >> 1
  const height = utils.getSoilY(cx)
  for (let index = 0; index < NitrateParticle.COUNT; index++) {
    particles.push({
      index,
      limit: [0, 0],
      layer: 0,
      shouldRender: false,
      velocity: vec2.create(prng.between(-1, 1), prng.between(2, 4)),
      position: vec2.create(prng.normal(-halfWidth, halfWidth), prng.between(0, height))
    })
  }
}

export function resize (canvas) {
  const { width } = canvas
  const halfWidth = width >> 1
  for (const particle of particles) {
    vec2.setX(particle.position, prng.normal(-halfWidth, halfWidth))
  }
}

export function destroy () {
  particles.splice(0, particles.length)
}

export default {
  getSoilX,
  getSoilWidth,
  renderSoil,
  renderNitrates,
  renderNitrateParticles,
  renderLines,
  init,
  resize,
  render,
  update,
  destroy
}
